import React, { useState, useRef } from 'react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import useElementToPdf from '../utils/useElementToPdf';
import { 
  Printer, 
  FileText, 
  Calendar,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Languages,
  Globe
} from 'lucide-react';
import { subDays, subMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth, format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import api from '../utils/api';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from 'react-i18next';
import { safeFormat, safeParseDate } from '../utils/dateUtils';

const PrintReportModal = ({
  isOpen,
  onClose,
  reportType, // 'revenues', 'expenses', 'inventory'
  barbers = [], // للإيرادات فقط
  currentBarber = null, // الحلاق الحالي إذا كان المستخدم حلاق
  isAdmin = false
}) => {
  const { language } = useLanguage();
  const { t } = useTranslation();
  const isArabic = language === 'ar';
  
  const [period, setPeriod] = useState('weekly');
  const [selectedBarber, setSelectedBarber] = useState('all');
  const [includeComparison, setIncludeComparison] = useState(true);
  const [includeCharts, setIncludeCharts] = useState(true);
  const [loading, setLoading] = useState(false);
  const [reportMode, setReportMode] = useState('pdf'); // 'pdf' or 'print'
  
  // Bilingual settings
  const [printLanguage, setPrintLanguage] = useState(language); // ar or en
  const [bilingualPrint, setBilingualPrint] = useState(false);
  
  // State and ref for printable report
  const [printReportData, setPrintReportData] = useState(null);
  const printReportRef = useRef();
  
  // react-to-print hook
  const handleReactPrint = useElementToPdf({
    contentRef: printReportRef,
    documentTitle: `${reportType}_report`,
    pageStyle: `
      @page { size: A4; margin: 15mm; }
      @media print {
        body { 
          -webkit-print-color-adjust: exact !important; 
          print-color-adjust: exact !important;
          direction: ${bilingualPrint ? 'rtl' : (printLanguage === 'ar' ? 'rtl' : 'ltr')};
        }
      }
    `
  });

  // Translations for bilingual support
  const TRANSLATIONS = {
    ar: {
      revenueReport: 'تقرير الإيرادات',
      expensesReport: 'تقرير المصروفات',
      inventoryReport: 'تقرير المخزون',
      report: 'تقرير',
      period: 'الفترة',
      barber: 'الحلاق',
      allBarbers: 'جميع الحلاقين',
      unspecified: 'غير محدد',
      weekly: 'أسبوعي',
      monthly: 'شهري',
      bimonthly: 'شهرين',
      total: 'الإجمالي',
      cash: 'كاش',
      card: 'مكينة',
      count: 'العدد',
      average: 'المتوسط',
      change: 'التغيير',
      currentPeriod: 'الفترة الحالية',
      previousPeriod: 'الفترة السابقة',
      from: 'من',
      to: 'إلى',
      printedOn: 'طُبع بتاريخ',
      keGentsSaloon: 'KE Gents Saloon'
    },
    en: {
      revenueReport: 'Revenue Report',
      expensesReport: 'Expenses Report',
      inventoryReport: 'Inventory Report',
      report: 'Report',
      period: 'Period',
      barber: 'Barber',
      allBarbers: 'All Barbers',
      unspecified: 'Unspecified',
      weekly: 'Weekly',
      monthly: 'Monthly',
      bimonthly: 'Bi-monthly',
      total: 'Total',
      cash: 'Cash',
      card: 'Card',
      count: 'Count',
      average: 'Average',
      change: 'Change',
      currentPeriod: 'Current Period',
      previousPeriod: 'Previous Period',
      from: 'From',
      to: 'To',
      printedOn: 'Printed on',
      keGentsSaloon: 'KE Gents Saloon'
    }
  };

  // Helper to get translated text
  const getText = (key, lang = printLanguage) => {
    return TRANSLATIONS[lang]?.[key] || TRANSLATIONS['ar'][key];
  };

  // Get bilingual text (Arabic / English)
  const getBilingualText = (key) => {
    if (bilingualPrint) {
      return `${TRANSLATIONS['ar'][key]} / ${TRANSLATIONS['en'][key]}`;
    }
    return getText(key);
  };

  const getReportTitle = () => {
    switch (reportType) {
      case 'revenues': return getBilingualText('revenueReport');
      case 'expenses': return getBilingualText('expensesReport');
      case 'inventory': return getBilingualText('inventoryReport');
      default: return getBilingualText('report');
    }
  };

  const getPeriodDates = (periodType, offset = 0) => {
    const now = new Date();
    let start, end;

    switch (periodType) {
      case 'weekly':
        const weekStart = startOfWeek(subDays(now, offset * 7), { weekStartsOn: 0 });
        const weekEnd = endOfWeek(subDays(now, offset * 7), { weekStartsOn: 0 });
        start = weekStart;
        end = weekEnd;
        break;
      case 'monthly':
        const monthStart = startOfMonth(subMonths(now, offset));
        const monthEnd = endOfMonth(subMonths(now, offset));
        start = monthStart;
        end = monthEnd;
        break;
      case 'bimonthly':
        const biMonthStart = startOfMonth(subMonths(now, offset * 2 + 1));
        const biMonthEnd = endOfMonth(subMonths(now, offset * 2));
        start = biMonthStart;
        end = biMonthEnd;
        break;
      default:
        start = startOfWeek(now, { weekStartsOn: 0 });
        end = endOfWeek(now, { weekStartsOn: 0 });
    }

    return { start, end };
  };

  const fetchReportData = async (startDate, endDate, barberId = null) => {
    const params = new URLSearchParams();
    params.append('start_date', startDate.toISOString());
    params.append('end_date', endDate.toISOString());
    if (barberId && barberId !== 'all') {
      params.append('barber_id', barberId);
    }

    try {
      let data = [];
      switch (reportType) {
        case 'revenues':
          const revRes = await api.get(`/revenues?${params.toString()}`);
          data = revRes.data;
          break;
        case 'expenses':
          const expRes = await api.get(`/expenses?${params.toString()}`);
          data = expRes.data;
          break;
        case 'inventory':
          const invRes = await api.get('/inventory');
          data = invRes.data;
          break;
        default:
          data = [];
      }
      return data;
    } catch (error) {
      console.error('Error fetching report data:', error);
      return [];
    }
  };

  const calculateStats = (data) => {
    if (!data || data.length === 0) {
      return { total: 0, count: 0, average: 0 };
    }

    const total = data.reduce((sum, item) => {
      if (reportType === 'inventory') {
        return sum + (item.quantity * (item.unit_price || 0));
      }
      return sum + (item.amount || 0);
    }, 0);

    return {
      total,
      count: data.length,
      average: data.length > 0 ? total / data.length : 0
    };
  };

  const downloadPDF = async () => {
    setLoading(true);
    try {
      const response = await api.post('/reports/generate-pdf', {
        report_type: reportType,
        period: period,
        barber_id: selectedBarber === 'all' ? null : selectedBarber,
        include_comparison: includeComparison,
        include_charts: includeCharts,
        lang: printLanguage,
        bilingual: bilingualPrint
      }, {
        responseType: 'blob'
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${reportType}_report_${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(isArabic ? 'تم تحميل التقرير بنجاح' : 'Report downloaded successfully');
      onClose();
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(isArabic ? 'فشل إنشاء التقرير' : 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const generatePrintContent = async () => {
    setLoading(true);
    try {
      const currentPeriod = getPeriodDates(period, 0);
      const previousPeriod = getPeriodDates(period, 1);

      const barberId = selectedBarber === 'all' ? null : selectedBarber;
      
      const currentData = await fetchReportData(currentPeriod.start, currentPeriod.end, barberId);
      const previousData = includeComparison 
        ? await fetchReportData(previousPeriod.start, previousPeriod.end, barberId)
        : [];

      const currentStats = calculateStats(currentData);
      const previousStats = includeComparison ? calculateStats(previousData) : null;

      const change = previousStats && previousStats.total > 0
        ? ((currentStats.total - previousStats.total) / previousStats.total * 100).toFixed(1)
        : 0;

      const barberName = selectedBarber === 'all' 
        ? getBilingualText('allBarbers')
        : barbers.find(b => b.id === selectedBarber)?.name || getBilingualText('unspecified');

      const periodLabels = {
        weekly: getBilingualText('weekly'),
        monthly: getBilingualText('monthly'),
        bimonthly: getBilingualText('bimonthly')
      };
      const periodLabel = periodLabels[period];

      // Set data for printable component
      setPrintReportData({
        currentPeriod,
        currentData,
        currentStats,
        previousStats,
        change,
        barberName,
        periodLabel,
        printLanguage,
        bilingualPrint
      });
      
      // Trigger print after setting data
      setTimeout(() => {
        handleReactPrint();
        toast.success(isArabic ? 'تم إنشاء التقرير بنجاح' : 'Report generated successfully');
        onClose();
      }, 300);
    } catch (error) {
      toast.error(isArabic ? 'فشل إنشاء التقرير' : 'Failed to generate report');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-card border-white/10 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Printer className="w-5 h-5 text-primary" />
            {getReportTitle()}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* اختيار الفترة */}
          <div>
            <label className="block text-sm font-medium mb-2">
              <Calendar className="w-4 h-4 inline-block me-2" />
              {isArabic ? 'الفترة الزمنية' : 'Time Period'}
            </label>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="bg-secondary/50 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-card border-white/10">
                <SelectItem value="weekly">{isArabic ? 'أسبوعي' : 'Weekly'}</SelectItem>
                <SelectItem value="monthly">{isArabic ? 'شهري' : 'Monthly'}</SelectItem>
                <SelectItem value="bimonthly">{isArabic ? 'شهرين' : 'Bi-monthly'}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* اختيار الحلاق (للإيرادات فقط) */}
          {reportType === 'revenues' && isAdmin && (
            <div>
              <label className="block text-sm font-medium mb-2">
                <FileText className="w-4 h-4 inline-block me-2" />
                {isArabic ? 'الحلاق' : 'Barber'}
              </label>
              <Select value={selectedBarber} onValueChange={setSelectedBarber}>
                <SelectTrigger className="bg-secondary/50 border-white/10">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-card border-white/10">
                  <SelectItem value="all">{isArabic ? 'جميع الحلاقين (الصالون)' : 'All Barbers (Salon)'}</SelectItem>
                  {barbers.map(barber => (
                    <SelectItem key={barber.id} value={barber.id}>
                      {barber.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* خيار المقارنة */}
          <div className="flex items-center gap-3 p-4 bg-secondary/30 rounded-lg">
            <Checkbox
              id="comparison"
              checked={includeComparison}
              onCheckedChange={setIncludeComparison}
            />
            <label htmlFor="comparison" className="flex-1 cursor-pointer">
              <div className="font-medium flex items-center gap-2">
                <BarChart3 className="w-4 h-4 text-primary" />
                {isArabic ? 'تضمين المقارنة' : 'Include Comparison'}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {isArabic ? 'مقارنة الفترة الحالية بالفترة السابقة' : 'Compare current period with previous period'}
              </p>
            </label>
          </div>

          {/* خيار الرسوم البيانية */}
          <div className="flex items-center gap-3 p-4 bg-secondary/30 rounded-lg">
            <Checkbox
              id="charts"
              checked={includeCharts}
              onCheckedChange={setIncludeCharts}
            />
            <label htmlFor="charts" className="flex-1 cursor-pointer">
              <div className="font-medium flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                {isArabic ? 'تضمين الرسوم البيانية' : 'Include Charts'}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {isArabic ? 'إضافة رسوم بيانية توضيحية للتقرير' : 'Add illustrative charts to the report'}
              </p>
            </label>
          </div>
          
          {/* Language Options Section */}
          <div className="border border-white/10 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-primary" />
              <span className="font-medium">{isArabic ? 'لغة التقرير' : 'Report Language'}</span>
            </div>
            
            {/* Language Selection */}
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                {isArabic ? 'اختر اللغة' : 'Select Language'}
              </span>
              <div className="flex gap-1">
                <button
                  type="button"
                  onClick={() => setPrintLanguage('ar')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    printLanguage === 'ar' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-secondary hover:bg-secondary/80'
                  }`}
                >
                  عربي
                </button>
                <button
                  type="button"
                  onClick={() => setPrintLanguage('en')}
                  className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                    printLanguage === 'en' 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-secondary hover:bg-secondary/80'
                  }`}
                >
                  English
                </button>
              </div>
            </div>
            
            {/* Bilingual Toggle */}
            <div className="flex items-center justify-between pt-2 border-t border-white/5">
              <div className="flex items-center gap-2">
                <Languages className="w-4 h-4 text-blue-500" />
                <span className="text-sm">{isArabic ? 'ثنائي اللغة (عربي/إنجليزي)' : 'Bilingual (Arabic/English)'}</span>
              </div>
              <Switch
                checked={bilingualPrint}
                onCheckedChange={setBilingualPrint}
              />
            </div>
          </div>
          
          {/* معلومات التقرير */}
          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <FileText className="w-4 h-4 text-primary" />
              <span className="font-medium">{isArabic ? 'معلومات التقرير' : 'Report Info'}</span>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• {isArabic ? 'الفترة' : 'Period'}: {period === 'weekly' ? (isArabic ? 'أسبوعي' : 'Weekly') : period === 'monthly' ? (isArabic ? 'شهري' : 'Monthly') : (isArabic ? 'شهرين' : 'Bi-monthly')}</li>
              {reportType === 'revenues' && <li>• {isArabic ? 'الحلاق' : 'Barber'}: {selectedBarber === 'all' ? (isArabic ? 'جميع الحلاقين' : 'All Barbers') : barbers.find(b => b.id === selectedBarber)?.name}</li>}
              <li>• {isArabic ? 'المقارنة' : 'Comparison'}: {includeComparison ? (isArabic ? 'نعم' : 'Yes') : (isArabic ? 'لا' : 'No')}</li>
              <li>• {isArabic ? 'الرسوم البيانية' : 'Charts'}: {includeCharts ? (isArabic ? 'نعم' : 'Yes') : (isArabic ? 'لا' : 'No')}</li>
              <li>• {isArabic ? 'اللغة' : 'Language'}: {bilingualPrint ? (isArabic ? 'ثنائي اللغة' : 'Bilingual') : (printLanguage === 'ar' ? 'العربية' : 'English')}</li>
              <li>• {isArabic ? 'التنسيق' : 'Format'}: PDF A4</li>
            </ul>
          </div>
        </div>
        
        {/* أزرار الإجراءات */}
        <div className="flex gap-3 mt-6">
          <Button
            onClick={downloadPDF}
            disabled={loading}
            className="flex-1 bg-primary hover:bg-primary/80 gap-2"
            data-testid="download-pdf-btn"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white"></div>
            ) : (
              <FileText className="w-4 h-4" />
            )}
            {isArabic ? 'تحميل PDF' : 'Download PDF'}
          </Button>
          <Button
            onClick={generatePrintContent}
            disabled={loading}
            variant="outline"
            className="flex-1 gap-2"
            data-testid="print-btn"
          >
            <Printer className="w-4 h-4" />
            {isArabic ? 'تنزيل PDF (مرئي)' : 'Download PDF (Visual)'}
          </Button>
        </div>
        
        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onClose} className="border-white/10">
            {isArabic ? 'إغلاق' : 'Close'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrintReportModal;
